<template>
<layout-content>
  <template #toolbar>
    <el-date-picker
      v-model="year"
      type="year"
      format="yyyy"
      value-format="yyyy"
      :clearable="false"
      :editable="false"
      size="small"
      style="width:100px;"
      @change="onSearchBtnClick"
    />

    <el-radio-group v-model="compareTypeVal" size="small" @change="onSearchBtnClick">
      <el-radio-button
        v-for="compareType in compareTypes"
        :key="compareType.value"
        :label="compareType.value"
      >{{compareType.label}}</el-radio-button>
    </el-radio-group>

    <el-select
      v-model="query.siteIds[0]"
      size="small"
      style="width:130px;"
      @change="onSiteIdChange"
    >
      <el-option
        v-for="site in filterSites"
        :key="site.id"
        :value="site.id"
        :label="site.name"
      />
    </el-select>

    <completed-query v-model="query.isAuditCompleted" @change="onAuditCompletedChanged" />

    <el-radio-group v-model="factorId" size="small" @change="onSelectedFactorChange">
      <el-radio-button
        v-for="siteFactor in siteFactors"
        :key="siteFactor.factorId"
        :label="siteFactor.factorId"
      >{{siteFactor.name}}</el-radio-button>
    </el-radio-group>
  </template>

  <template #body>
    <data-wrapper>
      <div class="charts-group-wrapper">
        <charts-group
          v-for="data in dataList"
          :key="data.value"
          :data="data"
          :show-header="dataList.length > 1"
          :factor-id="currentFactorId"
          :style="{
            width: dataList.length > 1 ? '250px' : '100%'
          }"
        />
      </div>
    </data-wrapper>
  </template>
</layout-content>
</template>

<script>
import BaseSiteFactorData from '../../../BaseSiteFactorData'
import DeviceCategoryConfig from '../../../../model/DeviceCategoryConfig'

import LayoutContent from '../../../../components/Layout/Content'
import DataWrapper from '../../../../components/Data/DataWrapper'
import ChartsGroup from './ChartsGroup'

const IAQI_LEVELS = [50, 100, 150, 200, 300]

export default {
  name: 'PollutionFeature',
  routeInfo: {
    label: '污染特征分析',
    icon: 'fa fa-users'
  },
  extends: BaseSiteFactorData,
  components: {ChartsGroup, DataWrapper, LayoutContent},
  data: () => ({
    compareTypeVal: 0,
    year: new Date().getFullYear().toString(),
    currentFactorId: null
  }),
  computed: {
    siteDeviceCategoryConfig () {
      return [
        new DeviceCategoryConfig(1, true),
        new DeviceCategoryConfig(process.env.SO2_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1001')),
        new DeviceCategoryConfig(process.env.NO_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1003')),
        new DeviceCategoryConfig(process.env.CO_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1005')),
        new DeviceCategoryConfig(process.env.O3_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1006')),
        new DeviceCategoryConfig(process.env.PM_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => /^100(7|8)$/.test(siteDeviceFactor.factorId)))
      ]
    },
    isSetAllFactorIds: () => 1,
    siteFactors () {
      const factorMap = this.siteFactorMap[this.query.siteIds[0]]
      return factorMap ? factorMap.map : {}
    },
    factorId: {
      get () {
        return this.query.factorIds[0]
      },
      set (factorId) {
        this.query.factorIds = [factorId]
      }
    },
    compareTypes: () => [{
      label: '同比',
      value: 0,
      gridColumns: 4
    }, {
      label: '环比',
      value: 2,
      gridColumns: 3
    }],
    compareType () {
      const {compareTypeVal} = this
      return this.compareTypes.find(compareType => compareType.value === compareTypeVal)
    }
  },
  methods: {
    setCurrentQuery () {
      const {siteFactors} = this

      if (!siteFactors[this.factorId]) {
        this.factorId = Object.values(siteFactors)[0].factorId
      }

      const {compareType, query} = this
      const year = this.year | 0
      const dateTime = new Date()
      dateTime.setFullYear(year)
      const endTime = dateTime.format('YYYY/12/31 23:59:59')
      dateTime.setFullYear(year - compareType.value)
      this.currentQuery = Object.freeze({
        beginTime: dateTime.format('YYYY/01/01 00:00:00'),
        endTime,
        dataType: 60,
        siteIds: query.siteIds,
        factorIds: query.factorIds,
        granularity: 1,
        isAuditCompleted: query.isAuditCompleted,
        isFuse: query.isFuse,
        isSiteMerge: false,
        isTimeMerge: false
      })
    },
    getData () {
      const {currentQuery} = this

      if (currentQuery.siteIds.length === 0) {
        return
      }

      this.changeLoadStatus(true)

      Promise.all([
        this.$api.Aqi.getAQIData({
          ...currentQuery,
          granularity: 24
        }),
        this.$api.Aqi.getAQIData(currentQuery)
      ]).then(this.setData).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    setData ([dailies, hourlies]) {
      const {currentQuery} = this
      const siteFactors = Object.values(this.siteFactors)
      const dailyMap = dailies.reduce((map, data) => {
        const {dataTime} = data
        map[new Date(dataTime).getTime()] = (data.pollutantData || []).reduce((map, data) => {
          if (!/^(O3)$/.test(data.rootFactorId)) {
            const {factorId, iaqi} = data
            const level = IAQI_LEVELS.findIndex(levelValue => iaqi <= levelValue)
            map[factorId] = {
              value: data.value,
              iaqi,
              level: level === -1 ? (IAQI_LEVELS.length - 1) : level
            }
          }

          return map
        }, {})

        return map
      }, {})

      const dataMap = hourlies.reduce((map, data) => {
        const {dataTime} = data
        map[new Date(dataTime).getTime()] = (data.pollutantData || []).reduce((map, data) => {
          if (!/^(H8O3)$/.test(data.rootFactorId)) {
            const {factorId} = data
            map[factorId] = {
              value: data.value,
              iaqi: data.iaqi
            }
          }

          return map
        }, {})
        return map
      }, {})
      const beginTime = new Date(currentQuery.beginTime)
      const beginTS = beginTime.getTime()
      const beginYear = beginTime.getFullYear()
      const endTime = new Date(currentQuery.endTime)
      const endTS = endTime.getTime()
      const endYear = endTime.getFullYear()
      const formatDataList = []

      for (let i = beginYear; i <= endYear; i++) {
        formatDataList.push(siteFactors.reduce((map, factor) => {
          map[factor.factorId] = new Array(6).fill(0)
          return map
        }, {
          value: i,
          label: `${i}年`,
          months: (() => {
            const months = []

            for (let i = 0; i < 12; i++) {
              const realMonth = i + 1
              months.push(siteFactors.reduce((map, factor) => {
                map[factor.factorId] = new Array(6).fill(0)
                return map
              }, {
                value: realMonth,
                name: `${realMonth}月`,
                days: []
              }))
            }

            return months
          })()
        }))
      }

      for (let i = beginTS; i < endTS; i += 3600000) {
        const date = new Date(i)
        const year = date.getFullYear()
        const yearData = formatDataList[year - beginYear]
        const month = date.getMonth()
        const monthData = yearData.months[month]
        const {days} = monthData
        const day = date.getDate()
        let dayData = days[day - 1]

        if (!dayData) {
          days.push(dayData = {
            value: day,
            name: day,
            hours: []
          })
        }

        const data = dataMap[i] || {}

        dayData.hours.push(siteFactors.reduce((map, factor) => {
          const {factorId} = factor
          const factorData = data[factorId]
          map[factorId] = factorData || {
            value: null,
            iaqi: null
          }
          return map
        }, {
          hour: date.getHours()
        }))

        const factorLevelData = dailyMap[i]

        if (factorLevelData) {
          siteFactors.forEach(factor => {
            const {factorId} = factor
            const levelData = factorLevelData[factorId]

            if (levelData) {
              const {level} = levelData
              yearData[factorId][level] += 1
              monthData[factorId][level] += 1
            }
          })
        }
      }

      this.dataList = Object.freeze(formatDataList)
      this.currentFactorId = this.factorId
    },
    onSiteIdChange () {
      const {siteFactors} = this

      if (!siteFactors[this.factorId]) {
        this.factorId = Object.values(siteFactors)[0].factorId
      }

      this.onSearchBtnClick()
    },
    onSelectedFactorChange () {
      this.currentFactorId = this.factorId
    }
  }
}
</script>

<style lang="scss" scoped>
.charts-group-wrapper {
  display: flex;
  gap: 20px;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/04AQI/09PollutionFeature/index.vue